
class HeimanStickyBuyBar extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.bar = this.querySelector('[data-sticky-bar]');
    this.priceElement = this.querySelector('[data-sticky-price]');
    this.compareElement = this.querySelector('[data-sticky-compare]');
    this.variantElement = this.querySelector('[data-sticky-variant]');
    this.addButton = this.querySelector('[data-sticky-add]');
    this.buyButton = this.querySelector('[data-sticky-buy]');
    this.statusElement = this.querySelector('[data-sticky-status]');
    this.imageElement = this.querySelector('.heiman-sticky-buy-bar__image img');

    this.form = null;
    this.variantCardsRoot = this.findVariantCardsRoot();

    try {
      const json = this.querySelector('[data-sticky-variants]');
      this.variants = JSON.parse(json?.textContent || '[]');
    } catch (error) {
      console.error('Heiman Sticky Buy Bar: Invalid variant data.', error);
      this.setButtonsDisabled(true);
      return;
    }

    this.variantMap = new Map(
      this.variants.map((variant) => [String(variant.id), variant])
    );

    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    const preferredVariantId = urlVariantId || this.dataset.initialVariantId || '';

    this.currentVariantId = this.variantMap.has(String(preferredVariantId))
      ? String(preferredVariantId)
      : String(this.dataset.initialVariantId || '');

    // 商品规格变化
    this.handleVariantChange = () => {
      requestAnimationFrame(() => {
        this.attachProductForm();

        const formVariantId = this.form?.querySelector('[name="id"]')?.value;

        if (formVariantId && this.variantMap.has(String(formVariantId))) {
          this.currentVariantId = String(formVariantId);
        }

        this.updateVariant();
      });
    };

    // 商品数量变化
    this.handleQuantityChange = () => {
      requestAnimationFrame(() => {
        this.updateVariant();
      });
    };

    // Heiman 自定义规格卡片事件
    this.handleHeimanVariantChange = (event) => {
      const detail = event.detail || {};

      if (
        detail.productId &&
        String(detail.productId) !== String(this.dataset.productId)
      ) {
        return;
      }

      const variantId = String(detail.variantId || '');

      if (!this.variantMap.has(variantId)) return;

      this.currentVariantId = variantId;

      const idInput = this.form?.querySelector('[name="id"]');

      if (idInput) {
        idInput.value = variantId;
        idInput.dispatchEvent(new Event('input', { bubbles: true }));
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.updateVariant();
    };

    // Shopify 主题规格变化
    this.handleThemeVariantUpdate = (event) => {
      const detail = event.detail || {};

      if (
        String(detail.data?.productId || '') !==
        String(this.dataset.productId)
      ) {
        return;
      }

      const variantId = String(detail.resource?.id || '');

      if (!this.variantMap.has(variantId)) return;

      this.currentVariantId = variantId;
      this.updateVariant();
    };

    this.handleScroll = () => this.updateVisibility();

    // 监听数量输入框
    this.variantCardsRoot?.addEventListener(
      'input',
      this.handleQuantityChange
    );

    this.variantCardsRoot?.addEventListener(
      'change',
      this.handleQuantityChange
    );

    // 监听数量加减按钮
    this.variantCardsRoot?.addEventListener(
      'click',
      this.handleQuantityChange
    );

    // 购买按钮
    this.addButton?.addEventListener('click', () => this.addToCart());
    this.buyButton?.addEventListener('click', () => this.buyNow());

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleScroll);

    document.addEventListener(
      'heiman:variant-change',
      this.handleHeimanVariantChange
    );

    document.addEventListener(
      'variant:update',
      this.handleThemeVariantUpdate
    );

    this.attachProductForm();
    this.updateVariant();
    this.updateVisibility();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);

    document.removeEventListener(
      'heiman:variant-change',
      this.handleHeimanVariantChange
    );

    document.removeEventListener(
      'variant:update',
      this.handleThemeVariantUpdate
    );

    if (this.variantCardsRoot && this.handleQuantityChange) {
      this.variantCardsRoot.removeEventListener(
        'input',
        this.handleQuantityChange
      );

      this.variantCardsRoot.removeEventListener(
        'change',
        this.handleQuantityChange
      );

      this.variantCardsRoot.removeEventListener(
        'click',
        this.handleQuantityChange
      );
    }

    if (this.form && this.handleVariantChange) {
      this.form.removeEventListener('change', this.handleVariantChange);
      this.form.removeEventListener('input', this.handleVariantChange);
    }

    this.initialized = false;
  }

  findVariantCardsRoot() {
    const productId = String(this.dataset.productId || '');

    const roots = Array.from(
      document.querySelectorAll(
        '.heiman-variant-cards-block[data-product-id]'
      )
    );

    return roots.find(
      (root) => String(root.dataset.productId) === productId
    ) || document.querySelector('[id^="heiman-variant-cards-"]') || null;
  }

  findProductForm() {
    const productId = String(this.dataset.productId || '');

    const forms = Array.from(
      document.querySelectorAll(
        'product-form-component form[action*="/cart/add"]'
      )
    );

    return forms.find((form) => {
      const component = form.closest('product-form-component');

      return component &&
        String(component.dataset.productId) === productId;
    }) || null;
  }

  attachProductForm() {
    const foundForm = this.findProductForm();

    if (!foundForm) {
      if (this.form && this.handleVariantChange) {
        this.form.removeEventListener('change', this.handleVariantChange);
        this.form.removeEventListener('input', this.handleVariantChange);
      }

      this.form = null;
      return false;
    }

    if (this.form === foundForm) return true;

    if (this.form && this.handleVariantChange) {
      this.form.removeEventListener('change', this.handleVariantChange);
      this.form.removeEventListener('input', this.handleVariantChange);
    }

    this.form = foundForm;

    this.form.addEventListener('change', this.handleVariantChange);
    this.form.addEventListener('input', this.handleVariantChange);

    const formVariantId = this.form.querySelector('[name="id"]')?.value;

    if (formVariantId && this.variantMap.has(String(formVariantId))) {
      this.currentVariantId = String(formVariantId);
    }

    return true;
  }

  getVariantId() {
    const formVariantId = this.form?.querySelector('[name="id"]')?.value;

    if (formVariantId && this.variantMap.has(String(formVariantId))) {
      return String(formVariantId);
    }

    return String(this.currentVariantId || '');
  }

  getCurrentVariant() {
    return this.variantMap.get(this.getVariantId());
  }

  getQuantityInput() {
    return this.variantCardsRoot?.querySelector('input[name="quantity"]')
      || this.form?.querySelector('input[name="quantity"]')
      || null;
  }

  getQuantity(variant) {
    const input = this.getQuantityInput();

    const min = Math.max(1, Number(variant?.quantity_min) || 1);
    const step = Math.max(1, Number(variant?.quantity_increment) || 1);

    const maxValue = Number(variant?.quantity_max);

    const max = Number.isFinite(maxValue) && maxValue > 0
      ? maxValue
      : Infinity;

    let quantity = Number.parseInt(input?.value || String(min), 10);

    if (!Number.isFinite(quantity)) quantity = min;

    quantity = Math.max(min, quantity);
    quantity = min + Math.round((quantity - min) / step) * step;
    quantity = Math.min(max, quantity);

    if (input) {
      input.min = String(min);
      input.step = String(step);

      if (Number.isFinite(max)) {
        input.max = String(max);
      } else {
        input.removeAttribute('max');
      }

      input.value = String(quantity);
    }

    return quantity;
  }

  getMoney(cents) {
    const currency = this.dataset.currency || 'USD';
    const locale = this.dataset.locale || 'en';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return '$' + ((Number(cents) || 0) / 100).toFixed(2);
    }
  }

  setButtonsDisabled(disabled) {
    if (this.addButton) this.addButton.disabled = disabled;
    if (this.buyButton) this.buyButton.disabled = disabled;
  }

  // 根据商品规格和购买数量更新总价
  updateVariant() {
    const variant = this.getCurrentVariant();

    if (!variant) {
      this.setButtonsDisabled(true);
      return;
    }

    this.currentVariantId = String(variant.id);

    const available = Boolean(variant.available);

    // 获取当前购买数量
    const quantity = this.getQuantity(variant);

    // 计算商品总价
    const totalPrice = Number(variant.price) * quantity;

    // 计算划线原价总额
    const compareAtPrice = Number(variant.compare_at_price) || 0;
    const totalComparePrice = compareAtPrice * quantity;

    // 更新底部显示价格
    if (this.priceElement) {
      this.priceElement.textContent = this.getMoney(totalPrice);
    }

    // 更新划线原价
    if (this.compareElement) {
      if (compareAtPrice > Number(variant.price)) {
        this.compareElement.textContent = this.getMoney(
          totalComparePrice
        );

        this.compareElement.hidden = false;
      } else {
        this.compareElement.textContent = '';
        this.compareElement.hidden = true;
      }
    }

    // 更新规格名称
    if (this.variantElement) {
      this.variantElement.textContent =
        variant.title === 'Default Title' ? '' : variant.title;
    }

    // 更新商品图片
    if (this.imageElement && variant.image) {
      this.imageElement.src = variant.image;
      this.imageElement.removeAttribute('srcset');
    }

    this.setButtonsDisabled(!available);
  }

  // 上下滚动都保持悬浮
  updateVisibility() {
    if (!this.bar) return;

    const offset = Number(this.dataset.scrollOffset || 150);
    const shouldShow = window.scrollY > offset;

    this.bar.classList.toggle('is-visible', shouldShow);

    this.bar.setAttribute(
      'aria-hidden',
      shouldShow ? 'false' : 'true'
    );
  }

  showStatus(message) {
    if (!this.statusElement) return;

    this.statusElement.textContent = message || '';
    this.statusElement.hidden = !message;
  }

  buildFormData(variantId, quantity) {
    const formData = this.form
      ? new FormData(this.form)
      : new FormData();

    formData.set('id', variantId);
    formData.set('quantity', String(quantity));

    const sectionIds = [
      ...new Set(
        Array.from(
          document.querySelectorAll(
            'cart-items-component[data-section-id]'
          )
        )
          .map((component) => component.dataset.sectionId)
          .filter(Boolean)
      ),
    ];

    if (sectionIds.length) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    return formData;
  }

  async requestAddToCart(variantId, quantity) {
    const formData = this.buildFormData(variantId, quantity);

    const response = await fetch(
      this.dataset.cartAddUrl || '/cart/add.js',
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      }
    );

    let result = {};

    try {
      result = await response.json();
    } catch (error) {
      throw new Error(
        'The store returned an invalid response. Please try again.'
      );
    }

    if (!response.ok || result.status) {
      throw new Error(
        result.description
        || result.message
        || 'Unable to add this item. Please check the selected option and quantity.'
      );
    }

    return result;
  }

  async addToCart() {
    if (!this.addButton || this.addButton.disabled) return;

    const variantId = this.getVariantId();
    const variant = this.variantMap.get(variantId);

    if (!variant || !variant.available) {
      this.showStatus('Please select an available option.');
      return;
    }

    const quantity = this.getQuantity(variant);
    const originalText = this.addButton.textContent;

    this.showStatus('');

    this.addButton.disabled = true;
    this.addButton.textContent = 'Adding…';

    try {
      const result = await this.requestAddToCart(
        variantId,
        quantity
      );

      let cart;

      try {
        const cartResponse = await fetch(
          this.dataset.cartJsonUrl || '/cart.js',
          {
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
            },
          }
        );

        if (cartResponse.ok) {
          cart = await cartResponse.json();
        }
      } catch (error) {
        // Cart refresh failure does not undo a successful add.
      }

      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.id,
            data: {
              source: 'product-form-component',
              productId: this.dataset.productId,
              variantId,
              itemCount: quantity,
              sections: result.sections || {},
            },
          },
        })
      );

      this.addButton.disabled = false;
      this.addButton.textContent = originalText;

      this.updateVariant();

      const drawer = document.querySelector(
        'cart-drawer-component'
      );

      if (drawer) {
        if (
          !drawer.hasAttribute('auto-open') &&
          typeof drawer.open === 'function'
        ) {
          drawer.open();
        }
      } else {
        window.location.assign(
          this.dataset.cartUrl || '/cart'
        );
      }

    } catch (error) {
      this.showStatus(
        error instanceof Error
          ? error.message
          : 'Unable to add this item. Please try again.'
      );

      this.addButton.disabled = false;
      this.addButton.textContent = originalText;

      this.updateVariant();
    }
  }

  async buyNow() {
    if (!this.buyButton || this.buyButton.disabled) return;

    const variantId = this.getVariantId();
    const variant = this.variantMap.get(variantId);

    if (!variant || !variant.available) {
      this.showStatus('Please select an available option.');
      return;
    }

    const quantity = this.getQuantity(variant);
    const originalText = this.buyButton.textContent;

    this.showStatus('');

    this.buyButton.disabled = true;
    this.buyButton.textContent = 'Processing…';

    try {
      await this.requestAddToCart(
        variantId,
        quantity
      );

      window.location.assign(
        this.dataset.checkoutUrl || '/checkout'
      );

    } catch (error) {
      this.showStatus(
        error instanceof Error
          ? error.message
          : 'Unable to proceed to checkout. Please try again.'
      );

      this.buyButton.disabled = false;
      this.buyButton.textContent = originalText;

      this.updateVariant();
    }
  }
}

if (!customElements.get('heiman-sticky-buy-bar')) {
  customElements.define(
    'heiman-sticky-buy-bar',
    HeimanStickyBuyBar
  );
}
